/**
 * ONU Store - Zustand store for ONU management
 */
import { create } from 'zustand';
import { onusAPI } from '../services/api';
import { getErrorMessage } from '../utils/errorMessages';
import type {
  ONUWithDetails,
  CreateONURequest,
  UpdateONURequest,
  ONUStats,
  AvailablePortsResponse
} from '../types';

interface ONUState {
  onus: ONUWithDetails[];
  stats: ONUStats;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchONUs: (projectId: number, filters?: { status?: string; customer_id?: number; pole_id?: number }) => Promise<void>;
  fetchStats: (projectId: number) => Promise<void>;
  getONUById: (onuId: number) => Promise<ONUWithDetails>;
  createONU: (data: CreateONURequest) => Promise<ONUWithDetails>;
  updateONU: (onuId: number, data: UpdateONURequest) => Promise<ONUWithDetails>;
  deleteONU: (onuId: number) => Promise<void>;
  assignCustomer: (onuId: number, customerId: number) => Promise<void>;
  unassignCustomer: (onuId: number) => Promise<void>;
  getAvailablePorts: (poleId: number) => Promise<AvailablePortsResponse>;
  clearError: () => void;
}

export const useONUStore = create<ONUState>((set, get) => ({
  onus: [],
  stats: {
    total: 0,
    active: 0,
    inactive: 0,
    offline: 0,
    pending: 0,
    with_customer: 0,
    without_customer: 0,
  },
  isLoading: false,
  error: null,

  fetchONUs: async (projectId: number, filters?: { status?: string; customer_id?: number; pole_id?: number }) => {
    set({ isLoading: true, error: null });
    try {
      const response = await onusAPI.getAll(projectId, filters);
      set({ onus: response.data, isLoading: false });
    } catch (error: any) {
      set({
        error: getErrorMessage(error, 'Nepodarilo sa načítať ONU.'),
        isLoading: false,
      });
      throw error;
    }
  },

  fetchStats: async (projectId: number) => {
    try {
      const response = await onusAPI.getStats(projectId);
      set({ stats: response.data });
    } catch (error: any) {
      console.error('Failed to fetch ONU stats:', error);
    }
  },

  getONUById: async (onuId: number) => {
    set({ isLoading: true, error: null });
    try {
      const response = await onusAPI.getById(onuId);
      set({ isLoading: false });
      return response.data;
    } catch (error: any) {
      set({
        error: getErrorMessage(error, 'Nepodarilo sa načítať ONU.'),
        isLoading: false,
      });
      throw error;
    }
  },

  createONU: async (data: CreateONURequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await onusAPI.create(data);
      const newONU = response.data;
      
      // Add to list
      set((state) => ({
        onus: [...state.onus, newONU],
        isLoading: false,
      }));
      
      // Refresh stats
      await get().fetchStats(data.project_id);
      
      return newONU;
    } catch (error: any) {
      set({
        error: getErrorMessage(error, 'Nepodarilo sa vytvoriť ONU.'),
        isLoading: false,
      });
      throw error;
    }
  },

  updateONU: async (onuId: number, data: UpdateONURequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await onusAPI.update(onuId, data);
      const updatedONU = response.data;
      
      // Update in list
      set((state) => ({
        onus: state.onus.map((onu) =>
          onu.id === onuId ? { ...onu, ...updatedONU } : onu
        ),
        isLoading: false,
      }));
      
      // Refresh stats
      await get().fetchStats(updatedONU.project_id);
      
      return updatedONU;
    } catch (error: any) {
      set({
        error: getErrorMessage(error, 'Nepodarilo sa uložiť zmeny ONU.'),
        isLoading: false,
      });
      throw error;
    }
  },

  deleteONU: async (onuId: number) => {
    set({ isLoading: true, error: null });
    try {
      const onu = get().onus.find((o) => o.id === onuId);
      await onusAPI.delete(onuId);
      
      // Remove from list
      set((state) => ({
        onus: state.onus.filter((o) => o.id !== onuId),
        isLoading: false,
      }));
      
      // Refresh stats
      if (onu) {
        await get().fetchStats(onu.project_id);
      }
    } catch (error: any) {
      set({
        error: getErrorMessage(error, 'Nepodarilo sa zmazať ONU.'),
        isLoading: false,
      });
      throw error;
    }
  },

  assignCustomer: async (onuId: number, customerId: number) => {
    set({ isLoading: true, error: null });
    try {
      const response = await onusAPI.assignCustomer(onuId, customerId);
      const updatedONU = response.data;
      
      // Update in list
      set((state) => ({
        onus: state.onus.map((onu) =>
          onu.id === onuId ? { ...onu, ...updatedONU } : onu
        ),
        isLoading: false,
      }));
      
      // Refresh stats
      await get().fetchStats(updatedONU.project_id);
    } catch (error: any) {
      set({
        error: getErrorMessage(error, 'Nepodarilo sa priradiť zákazníka.'),
        isLoading: false,
      });
      throw error;
    }
  },

  unassignCustomer: async (onuId: number) => {
    set({ isLoading: true, error: null });
    try {
      const response = await onusAPI.unassignCustomer(onuId);
      const updatedONU = response.data;
      
      // Update in list
      set((state) => ({
        onus: state.onus.map((onu) =>
          onu.id === onuId ? { ...onu, ...updatedONU } : onu
        ),
        isLoading: false,
      }));
      
      // Refresh stats
      await get().fetchStats(updatedONU.project_id);
    } catch (error: any) {
      set({
        error: getErrorMessage(error, 'Nepodarilo sa zrušiť priradenie zákazníka.'),
        isLoading: false,
      });
      throw error;
    }
  },

  getAvailablePorts: async (poleId: number) => {
    try {
      const response = await onusAPI.getAvailablePorts(poleId);
      return response.data;
    } catch (error: any) {
      set({
        error: getErrorMessage(error, 'Nepodarilo sa načítať voľné porty.'),
      });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));

